import { useEffect, useMemo, useState } from 'react'
import { Bug, Camera, CheckCircle, MapPin, Send, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { useBugButtonVisibility } from '../../hooks/useBugButtonVisibility'
import { captureDownsizedScreenshot, type CapturedScreenshot } from '../../lib/screenshot'
import {
  createSmeFeedback,
  type SmeFeedbackCategory,
  type SmeFeedbackPin,
  type SmeFeedbackSeverity,
} from '../../lib/services/smeFeedback'

const DRAFT_STORAGE_KEY = 'sme_feedback_overlay_draft_v1'

type SmeFeedbackDraft = {
  page_path: string
  category: SmeFeedbackCategory
  severity: SmeFeedbackSeverity
  note: string
  pins: SmeFeedbackPin[]
  screenshot: CapturedScreenshot | null
}

const FEEDBACK_CATEGORIES: SmeFeedbackCategory[] = [
  'bug',
  'workflow_confusion',
  'missing_data',
  'content_issue',
  'visual_issue',
  'other',
]

const FEEDBACK_SEVERITIES: SmeFeedbackSeverity[] = ['blocker', 'high', 'medium', 'low']

function routeLabel(pathname: string): string {
  return pathname.split('/').filter(Boolean).join(' / ') || 'Overview'
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isCategory(value: unknown): value is SmeFeedbackCategory {
  return typeof value === 'string' && FEEDBACK_CATEGORIES.includes(value as SmeFeedbackCategory)
}

function isSeverity(value: unknown): value is SmeFeedbackSeverity {
  return typeof value === 'string' && FEEDBACK_SEVERITIES.includes(value as SmeFeedbackSeverity)
}

function parsePins(value: unknown): SmeFeedbackPin[] {
  if (!Array.isArray(value)) return []
  return value.filter(isRecord).map((pin) => ({
    id: typeof pin.id === 'string' ? pin.id : crypto.randomUUID(),
    x: typeof pin.x === 'number' ? pin.x : 0,
    y: typeof pin.y === 'number' ? pin.y : 0,
    note: typeof pin.note === 'string' ? pin.note : '',
    created_at: typeof pin.created_at === 'string' ? pin.created_at : new Date().toISOString(),
  }))
}

function parseScreenshot(value: unknown): CapturedScreenshot | null {
  if (!isRecord(value)) return null
  if (
    typeof value.data_url !== 'string' ||
    typeof value.width !== 'number' ||
    typeof value.height !== 'number' ||
    typeof value.bytes !== 'number' ||
    typeof value.captured_at !== 'string'
  ) {
    return null
  }
  return {
    data_url: value.data_url,
    width: value.width,
    height: value.height,
    bytes: value.bytes,
    captured_at: value.captured_at,
  }
}

function readFeedbackDraft(pagePath: string): SmeFeedbackDraft | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed) || parsed.page_path !== pagePath) return null
    return {
      page_path: pagePath,
      category: isCategory(parsed.category) ? parsed.category : 'bug',
      severity: isSeverity(parsed.severity) ? parsed.severity : 'medium',
      note: typeof parsed.note === 'string' ? parsed.note : '',
      pins: parsePins(parsed.pins),
      screenshot: parseScreenshot(parsed.screenshot),
    }
  } catch {
    return null
  }
}

function writeFeedbackDraft(draft: SmeFeedbackDraft): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft))
  } catch {
    /* ignore storage failures; the current in-memory draft still works */
  }
}

function clearFeedbackDraft(pagePath: string): void {
  if (typeof window === 'undefined') return
  try {
    const raw = window.sessionStorage.getItem(DRAFT_STORAGE_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (isRecord(parsed) && parsed.page_path === pagePath) {
      window.sessionStorage.removeItem(DRAFT_STORAGE_KEY)
    }
  } catch {
    window.sessionStorage.removeItem(DRAFT_STORAGE_KEY)
  }
}

export function SmeFeedbackOverlay() {
  const location = useLocation()
  const [bugButtonEnabled] = useBugButtonVisibility()
  const pagePath = `${location.pathname}${location.search}${location.hash}`
  const initialDraft = readFeedbackDraft(pagePath)
  const [open, setOpen] = useState(false)
  const [captureMode, setCaptureMode] = useState(false)
  const [category, setCategory] = useState<SmeFeedbackCategory>(initialDraft?.category ?? 'bug')
  const [severity, setSeverity] = useState<SmeFeedbackSeverity>(initialDraft?.severity ?? 'medium')
  const [note, setNote] = useState(initialDraft?.note ?? '')
  const [pins, setPins] = useState<SmeFeedbackPin[]>(initialDraft?.pins ?? [])
  const [screenshot, setScreenshot] = useState<CapturedScreenshot | null>(initialDraft?.screenshot ?? null)
  const [draftPagePath, setDraftPagePath] = useState(pagePath)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [capturing, setCapturing] = useState(false)

  const title = useMemo(() => routeLabel(location.pathname), [location.pathname])

  useEffect(() => {
    setCaptureMode(false)
    const draft = readFeedbackDraft(pagePath)
    setCategory(draft?.category ?? 'bug')
    setSeverity(draft?.severity ?? 'medium')
    setNote(draft?.note ?? '')
    setPins(draft?.pins ?? [])
    setScreenshot(draft?.screenshot ?? null)
    setDraftPagePath(pagePath)
  }, [pagePath])

  useEffect(() => {
    if (draftPagePath !== pagePath) return
    const hasDraft = Boolean(note.trim()) || pins.length > 0 || screenshot !== null
    if (!hasDraft) {
      clearFeedbackDraft(pagePath)
      return
    }
    writeFeedbackDraft({
      page_path: pagePath,
      category,
      severity,
      note,
      pins,
      screenshot,
    })
  }, [category, draftPagePath, note, pagePath, pins, screenshot, severity])

  if (!bugButtonEnabled) return null

  async function handleCaptureScreenshot() {
    setError(null)
    setOpen(false)
    setCapturing(true)
    // Safety timeout: html2canvas can hang on complex DOM on mobile Safari.
    // If it takes longer than 12s, abort and surface an error instead of
    // leaving the user with a locked-looking screen.
    const timeout = new Promise<never>((_, reject) =>
      window.setTimeout(() => reject(new Error('Screenshot timed out after 12s — try again or skip the screenshot.')), 12_000)
    )
    try {
      await new Promise((resolve) => setTimeout(resolve, 50))
      const captured = await Promise.race([captureDownsizedScreenshot(), timeout])
      setScreenshot(captured as CapturedScreenshot)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to capture screenshot.')
    } finally {
      setCapturing(false)
      setOpen(true)
    }
  }

  async function handleSubmit() {
    setError(null)
    setSubmitted(false)
    if (!note.trim()) {
      setError('Add a note before submitting.')
      return
    }
    setSubmitting(true)
    try {
      await createSmeFeedback({
        page_path: pagePath,
        route_name: title,
        category,
        severity,
        note: note.trim(),
        pins,
        page_metadata: {
          title: document.title,
          workflow: 'sme_review',
          surface: 'in_app_notebox',
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight,
            device_pixel_ratio: window.devicePixelRatio,
          },
          screenshot,
        },
      })
      setSubmitted(true)
      setNote('')
      setPins([])
      setScreenshot(null)
      clearFeedbackDraft(pagePath)
      window.setTimeout(() => {
        setSubmitted(false)
        setOpen(false)
      }, 1200)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit feedback.')
    } finally {
      setSubmitting(false)
    }
  }

  // Visible "Capturing..." overlay while html2canvas runs. Without this the
  // page looks frozen during capture (main thread is blocked) and users think
  // the screen has locked up. data-screenshot-ignore keeps it out of the
  // captured image itself.
  if (capturing) {
    return (
      <div
        data-screenshot-ignore="true"
        className="fixed inset-0 z-[95] flex items-center justify-center bg-slate-950/60"
      >
        <div className="rounded-lg bg-white px-6 py-5 text-center shadow-2xl">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-rose-600" />
          <p className="text-sm font-bold text-slate-900">Capturing screenshot…</p>
          <p className="mt-1 text-xs text-slate-500">One moment — the page may pause briefly.</p>
        </div>
      </div>
    )
  }

  if (captureMode) {
    return (
      <button
        type="button"
        className="fixed inset-0 z-[95] cursor-crosshair bg-slate-950/10 text-left"
        onClick={(event) => {
          const pin: SmeFeedbackPin = {
            id: crypto.randomUUID(),
            x: event.clientX / Math.max(window.innerWidth, 1),
            y: event.clientY / Math.max(window.innerHeight, 1),
            note: '',
            created_at: new Date().toISOString(),
          }
          setPins((current) => [...current, pin])
          setCaptureMode(false)
          setOpen(true)
        }}
      >
        <span className="fixed left-1/2 top-4 -translate-x-1/2 rounded-lg bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-xl">
          Tap the exact issue location
        </span>
      </button>
    )
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 z-[70] flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-xs font-extrabold text-white shadow-lg md:bottom-6"
      >
        <Bug size={15} />
        Report Bugs Here
      </button>

      {open && (
        <div
          data-screenshot-ignore="true"
          className="fixed inset-0 z-[90] flex items-end justify-center overflow-y-auto bg-slate-950/45 p-3 md:items-center"
        >
          <div className="max-h-[calc(100vh-1.5rem)] w-full max-w-md overflow-y-auto rounded-lg bg-white p-4 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[11px] font-extrabold uppercase text-rose-600">SME Review Feedback</p>
                <h2 className="text-lg font-extrabold text-slate-900">Report Bugs Here</h2>
                <p className="mt-1 text-xs font-semibold text-slate-500">{title}</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100">
                <X size={18} />
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <select
                value={category}
                onChange={(event) => setCategory(event.target.value as SmeFeedbackCategory)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold"
              >
                <option value="bug">Bug</option>
                <option value="workflow_confusion">Workflow confusion</option>
                <option value="missing_data">Missing data</option>
                <option value="content_issue">Content issue</option>
                <option value="visual_issue">Visual issue</option>
                <option value="other">Other</option>
              </select>
              <select
                value={severity}
                onChange={(event) => setSeverity(event.target.value as SmeFeedbackSeverity)}
                className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold"
              >
                <option value="blocker">Blocker</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>

            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What should we fix? Do not include PHI."
              rows={4}
              maxLength={5000}
              className="mt-3 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-100"
            />

            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false)
                  setCaptureMode(true)
                }}
                className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
              >
                <MapPin size={14} />
                Add pin
              </button>
              <button
                type="button"
                onClick={() => void handleCaptureScreenshot()}
                className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
              >
                <Camera size={14} />
                Capture page
              </button>
            </div>

            {screenshot && (
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 p-2">
                <img
                  src={screenshot.data_url}
                  alt="Captured page screenshot"
                  className="h-auto max-h-36 w-36 rounded border border-slate-200 object-contain"
                />
                <button
                  type="button"
                  onClick={() => void handleCaptureScreenshot()}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700"
                >
                  Retake
                </button>
              </div>
            )}

            {pins.length > 0 && (
              <div className="mt-3 max-h-32 space-y-2 overflow-y-auto rounded-lg bg-slate-50 p-2">
                {pins.map((pin, index) => (
                  <div key={pin.id} className="flex items-center gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-600 text-[11px] font-bold text-white">
                      {index + 1}
                    </span>
                    <input
                      value={pin.note}
                      onChange={(event) => setPins((current) => current.map((item) => (
                        item.id === pin.id ? { ...item, note: event.target.value } : item
                      )))}
                      placeholder="Pin note"
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
                    />
                  </div>
                ))}
              </div>
            )}

            {error && <p className="mt-3 text-xs font-bold text-red-600">{error}</p>}
            {submitted && (
              <p className="mt-3 flex items-center gap-1 text-xs font-bold text-emerald-700">
                <CheckCircle size={14} />
                Feedback submitted
              </p>
            )}

            <button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-3 text-sm font-extrabold text-white disabled:opacity-50"
            >
              <Send size={15} />
              {submitting ? 'Submitting...' : 'Submit feedback'}
            </button>
          </div>
        </div>
      )}
    </>
  )
}
